import { useCallback, useEffect, useState } from 'react';
import { Clock, Fingerprint, KeyRound, LayoutGrid, Loader2, Plus, Trash2, UserRound } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { NotConfigured } from '@/components/ui/not-configured';
import { notify } from '@/components/ui/notify';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useFeature } from '@/hooks/useIntegrationStatus';
import { sessionLengthText } from '@/components/entry/session';
import { describePasskeyError, isPasskeySupported, writePasskeyChoice } from '@/components/entry/passkeys';
import { deviceName } from '@/components/entry/PasskeyOffer';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatDateTime } from '@/lib/format';
import { userInitials } from '@/components/entry/user-initials';

function Row({ icon: Icon, label, children, testId }) {
  return (
    <div className="flex items-start gap-3 px-5 py-3" data-testid={testId}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold text-ink-3">{label}</p>
        <div className="mt-0.5 text-[13.5px] leading-snug text-ink">{children}</div>
      </div>
    </div>
  );
}

/**
 * Passkeys (01 #9): the person's own passkeys — name, added, last used — with Add (on this device) and Remove.
 * While the feature is not set up / switched off it says so (the list still shows, so old ones can be removed).
 */
function PasskeysSection({ open, user }) {
  const { registerPasskey } = useAuth() || {};
  const confirm = useConfirm();
  const passkeys = useFeature('passkeys');
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/auth/passkeys');
      setItems(res.data?.items || []);
      setFailed('');
    } catch (err) {
      setFailed(getErrorMessage(err, 'Could not load your passkeys'));
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const add = async () => {
    setBusy('add');
    try {
      const created = await registerPasskey({ name: deviceName() });
      writePasskeyChoice(user?.id, 'registered');
      setItems((current) => [created, ...(current || [])]);
      notify.success('Passkey added', { description: `Next time choose “Sign in with a passkey” on ${created.name}.` });
    } catch (err) {
      const browser = describePasskeyError(err);
      if (browser.kind !== 'cancelled') notify.error('Couldn’t add a passkey', { description: browser.message || getErrorMessage(err) });
    } finally {
      setBusy('');
    }
  };

  const remove = async (item) => {
    const ok = await confirm({
      title: `Remove the passkey “${item.name}”?`,
      description: 'That device will need your password (or a new passkey) to sign in.',
      confirmLabel: 'Remove passkey',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(item.id);
    try {
      await api.delete(`/auth/passkeys/${encodeURIComponent(item.id)}`);
      setItems((current) => (current || []).filter((entry) => entry.id !== item.id));
      writePasskeyChoice(user?.id, '');
      notify.success(`Passkey “${item.name}” removed`);
    } catch (err) {
      notify.error('Couldn’t remove the passkey', { description: getErrorMessage(err) });
    } finally {
      setBusy('');
    }
  };

  const canAdd = passkeys.enabled && isPasskeySupported();
  return (
    <Row icon={Fingerprint} label="Passkeys (Face ID / fingerprint)" testId="profile-passkeys">
      {!passkeys.loading && !passkeys.enabled ? (
        <NotConfigured feature="passkeys" layout="inline" className="mt-1" testId="profile-passkeys-not-configured" />
      ) : null}
      {failed ? <p className="text-[13px] text-status-red-ink">{failed}</p> : null}
      {items && items.length ? (
        <ul className="mt-1 divide-y divide-[color:var(--line-2)] rounded-control border border-line">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-2 px-3 py-2" data-testid={`profile-passkey-${item.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-ink">{item.name}</p>
                <p className="text-[12px] text-ink-3">
                  Added {formatDate(item.created_at)}{item.last_used_at ? ` · last used ${formatDate(item.last_used_at)}` : ' · not used yet'}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => remove(item)}
                disabled={Boolean(busy)}
                aria-label={`Remove passkey ${item.name}`}
                data-testid={`profile-passkey-remove-${item.id}`}
              >
                {busy === item.id ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Trash2 aria-hidden="true" />}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      {items && !items.length && passkeys.enabled ? (
        <p className="text-[13px] text-ink-2">None yet. Add one to sign in on this device without typing your password.</p>
      ) : null}
      {canAdd ? (
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={add} disabled={Boolean(busy)} data-testid="profile-passkey-add">
          {busy === 'add' ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Plus aria-hidden="true" />}
          Add a passkey on this device
        </Button>
      ) : null}
    </Row>
  );
}

/**
 * Profile — who is signed in, what they can open, when this session ends (and whether this device is kept
 * signed in), and their passkeys. Names and roles are managed in Admin › Users and passwords are reset by a
 * Super Admin (no self-service yet).
 */
export function ProfileDialog({ open, onOpenChange, user, modules = [] }) {
  const { sessionTimes, rememberUntil } = useAuth() || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] max-w-[460px] gap-0 overflow-y-auto rounded-[18px] border-line bg-surface p-0 text-ink" data-testid="profile-dialog">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b border-line-2 px-5 pb-4 pt-5 text-left">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-navy text-[14px] font-bold text-white" aria-hidden="true">
            {userInitials(user?.name)}
          </span>
          <div className="min-w-0">
            <DialogTitle className="truncate font-heading text-[17px] font-bold text-ink">{user?.name || 'Your profile'}</DialogTitle>
            <DialogDescription className="truncate text-[12.5px] text-ink-3">{user?.role}</DialogDescription>
          </div>
        </DialogHeader>
        <div className="divide-y divide-[color:var(--line-2)]">
          <Row icon={UserRound} label="Sign-in name">
            <span className="break-all">{user?.email || '—'}</span>
          </Row>
          <Row icon={LayoutGrid} label="You can open">
            {modules.length ? modules.join(' · ') : '—'}
          </Row>
          <Row icon={Clock} label="This session" testId="profile-session">
            {rememberUntil ? (
              <>Kept signed in on this device until <b className="font-semibold">{formatDate(new Date(rememberUntil))}</b> · Sign out to forget this device</>
            ) : sessionTimes?.exp
              ? <>Ends {formatDateTime(new Date(sessionTimes.exp), { relativeDay: true })} · sessions last {sessionLengthText(sessionTimes)}</>
              : `Sessions last ${sessionLengthText(null)}`}
          </Row>
          <PasskeysSection open={open} user={user} />
          <Row icon={KeyRound} label="Password">
            Passwords are reset by a <b className="font-semibold">Super Admin</b> from <b className="font-semibold">Admin › Users</b>.
            Your name and role are changed there too.
          </Row>
        </div>
      </DialogContent>
    </Dialog>
  );
}
